/**
 * Leaderboard — host-aggregated score table for independent-mode games.
 *
 * The pattern: each peer runs its own local simulation (every player has
 * their own balloons in their own AR space, generated from the shared
 * seed). When something scoreable happens locally (a balloon pops), the
 * peer calls `lb.report(1)`. The Leaderboard:
 *
 *   - on a CLIENT: emits a `__lb__report` event to the host AND
 *     optimistically bumps the local copy so the on-screen score feels
 *     instant
 *   - on the HOST: directly aggregates the delta into the authoritative
 *     scores map AND re-broadcasts a `__lb__update` event to every peer
 *
 * Every peer receives the host-broadcast `__lb__update` and overwrites
 * its local map with the authoritative state, firing `onUpdate` subscribers.
 *
 * The double-underscore event names are deliberately namespaced so they
 * don't show up in the user's own `onSdkEvent` handler.
 *
 * Usage:
 *
 *   import { Leaderboard } from '@platform/sdk';
 *
 *   class GameScene extends SdkGameScene {
 *     start() {
 *       this.lb = new Leaderboard({ sdk: this.sdk });
 *       this.lb.onUpdate(({ scores, leader }) => this.refreshHud(scores, leader));
 *     }
 *     popBalloon() {
 *       this.lb.report(1);
 *     }
 *   }
 *
 * The Leaderboard is intentionally **not** specific to AR or to
 * independent mode — synchronized-mode games can use it too if they want
 * a per-player counter on top of the shared world state.
 */

const REPORT_EVENT = '__lb__report';
const UPDATE_EVENT = '__lb__update';

export class Leaderboard {
  /**
   * @param {object} opts
   * @param {object} opts.sdk - the SDK handle from `this.sdk` in a GameScene
   *   (must expose `isHost`, `players`, `localPlayerId`, `on(name, cb)`,
   *   `emit(name, payload)`).
   * @param {(snapshot: {scores: Array, leader: object|null}) => void} [opts.onUpdate]
   */
  constructor(opts) {
    if (!opts || !opts.sdk) throw new Error('Leaderboard requires { sdk }');
    this.sdk = opts.sdk;

    /** @type {Map<string, { id: string, name: string, score: number }>} */
    this._rows = new Map();
    /** @type {Array<(snap: any) => void>} */
    this._subscribers = [];
    /** @type {Array<() => void>} */
    this._unsubscribers = [];

    if (opts.onUpdate) this._subscribers.push(opts.onUpdate);

    // Seed the table from the current roster.
    for (const p of (this.sdk.players || [])) {
      this._rows.set(p.id, { id: p.id, name: p.name || p.id, score: 0 });
    }

    // Wire to the SDK event bus.
    this._unsubscribers.push(
      this.sdk.on(UPDATE_EVENT, (payload) => this._applyUpdate(payload))
    );
    if (this.sdk.isHost) {
      this._unsubscribers.push(
        this.sdk.on(REPORT_EVENT, (payload) => this._applyReport(payload))
      );
    }
    // Roster changes (mid-game join/leave).
    this._unsubscribers.push(
      this.sdk.on('players-changed', (list) => this._applyRosterChange(list))
    );
  }

  // ───────────────────────────────────────────────────────── public API

  /**
   * Report a score change for the local player. Accepts:
   *   lb.report(1)              // delta = 1
   *   lb.report({ delta: 5 })   // explicit delta
   *   lb.report({ playerId: 'p_xyz', delta: 3 })   // host can score for anyone
   */
  report(arg) {
    let delta = 0;
    let playerId = this.sdk.localPlayerId;
    if (typeof arg === 'number') {
      delta = arg;
    } else if (arg && typeof arg === 'object') {
      if (typeof arg.delta === 'number') delta = arg.delta;
      if (arg.playerId) playerId = arg.playerId;
    }
    if (!delta) return;

    if (this.sdk.isHost) {
      this._mutate(playerId, (row) => { row.score += delta; });
      this._broadcast();
    } else {
      // Optimistic local update so the HUD feels instant.
      this._mutate(playerId, (row) => { row.score += delta; });
      this._notifySubscribers();
      // Tell the host the canonical truth.
      this.sdk.emit(REPORT_EVENT, { playerId, delta });
    }
  }

  /**
   * Set the local player's absolute score. Same shape as `report` for the
   * argument: `lb.set(42)` or `lb.set({ playerId, value: 42 })`.
   */
  set(arg) {
    let value = 0;
    let playerId = this.sdk.localPlayerId;
    if (typeof arg === 'number') {
      value = arg;
    } else if (arg && typeof arg === 'object') {
      if (typeof arg.value === 'number') value = arg.value;
      if (arg.playerId) playerId = arg.playerId;
    }

    if (this.sdk.isHost) {
      this._mutate(playerId, (row) => { row.score = value; });
      this._broadcast();
    } else {
      this._mutate(playerId, (row) => { row.score = value; });
      this._notifySubscribers();
      this.sdk.emit(REPORT_EVENT, { playerId, set: value });
    }
  }

  /** Get one player's current score (0 if unknown). */
  get(playerId) {
    const row = this._rows.get(playerId || this.sdk.localPlayerId);
    return row ? row.score : 0;
  }

  /** Returns an array sorted by score descending: `[{ id, name, score }, ...]`. */
  getAll() {
    const rows = Array.from(this._rows.values()).map((r) => ({
      id: r.id, name: r.name, score: r.score
    }));
    rows.sort((a, b) => b.score - a.score);
    return rows;
  }

  /** The current leader, or null if everyone is at 0. */
  getWinner() {
    const all = this.getAll();
    if (all.length === 0) return null;
    if (all[0].score <= 0) return null;
    return all[0];
  }

  /** Subscribe to updates. Returns an unsubscribe function. */
  onUpdate(cb) {
    if (typeof cb !== 'function') return () => {};
    this._subscribers.push(cb);
    return () => {
      const i = this._subscribers.indexOf(cb);
      if (i !== -1) this._subscribers.splice(i, 1);
    };
  }

  /** Detach from the SDK event bus. */
  destroy() {
    for (const off of this._unsubscribers) {
      try { off(); } catch (_) { /* ignore */ }
    }
    this._unsubscribers = [];
    this._subscribers = [];
  }

  // ────────────────────────────────────────────────────── internals

  _mutate(playerId, fn) {
    let row = this._rows.get(playerId);
    if (!row) {
      row = { id: playerId, name: playerId, score: 0 };
      this._rows.set(playerId, row);
    }
    fn(row);
  }

  _broadcast() {
    if (!this.sdk.isHost) return;
    const payload = {
      rows: Array.from(this._rows.values()).map((r) => ({
        id: r.id, name: r.name, score: r.score
      }))
    };
    // The host's own UPDATE_EVENT subscriber will fire (via _applyUpdate)
    // when the event round-trips through the bus too — but we want
    // listeners to see the new state immediately, not next tick. So we
    // notify subscribers locally now AND emit for clients.
    this._notifySubscribers();
    this.sdk.emit(UPDATE_EVENT, payload);
  }

  _applyReport(payload) {
    // Host-only path: a client said "I scored". Aggregate + rebroadcast.
    if (!this.sdk.isHost || !payload || !payload.playerId) return;
    if (typeof payload.set === 'number') {
      this._mutate(payload.playerId, (r) => { r.score = payload.set; });
    } else if (typeof payload.delta === 'number') {
      this._mutate(payload.playerId, (r) => { r.score += payload.delta; });
    } else {
      return;
    }
    this._broadcast();
  }

  _applyUpdate(payload) {
    // Both clients AND the host receive this when the host broadcasts.
    // We replace the table with the authoritative copy — except for the
    // local player's row, where we keep the higher of the two scores.
    //
    // Why max: imagine a client pops 3 balloons in 60 ms (faster than
    // the round trip). Each pop optimistically bumps the local score to
    // 1, 2, 3 and sends a separate REPORT. The host processes them
    // serially and emits a fresh UPDATE after each (containing 1, 2, 3
    // in turn). The first UPDATE arrives at the client when local is
    // already 3; if we replaced verbatim the score would visibly drop
    // to 1, then 2, then 3 — a rubber-band. Since scores in a
    // monotonic-increasing leaderboard never decrease, taking the max
    // converges correctly without rubber-banding.
    if (!payload || !Array.isArray(payload.rows)) return;
    const localId = this.sdk.localPlayerId;
    const next = new Map();
    for (const r of payload.rows) {
      let score = r.score || 0;
      if (r.id === localId) {
        const existing = this._rows.get(localId);
        if (existing && existing.score > score) score = existing.score;
      }
      next.set(r.id, { id: r.id, name: r.name || r.id, score });
    }
    this._rows = next;
    this._notifySubscribers();
  }

  _applyRosterChange(list) {
    if (!Array.isArray(list)) return;
    const next = new Map();
    for (const p of list) {
      const existing = this._rows.get(p.id);
      next.set(p.id, {
        id: p.id,
        name: p.name || p.id,
        // Preserve scores for players who are still here.
        score: existing ? existing.score : 0
      });
    }
    this._rows = next;
    this._notifySubscribers();
  }

  _notifySubscribers() {
    const snap = { scores: this.getAll(), leader: this.getWinner() };
    for (const cb of this._subscribers.slice()) {
      try { cb(snap); } catch (err) {
        if (typeof console !== 'undefined') console.error('[leaderboard subscriber]', err);
      }
    }
  }
}
